import React from 'react';
import styles from '../../../styles/DocumentManagement.module.css';
import { DocumentWithType } from '../../../types/Document';
import { UserDetails } from '../../../types/User';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import { error } from '../../../utils/errorLogger';
import UserReminders from './UserReminders';
import OverviewHeader from './OverviewHeader';
import RecentDocuments from './RecentDocuments';

interface DocumentsOverviewProps {
  userDetails: UserDetails | null;
  onReminderClick?: (reminder: any) => void;
}

const DocumentsOverview: React.FC<DocumentsOverviewProps> = ({
  userDetails,
  onReminderClick,
}) => {
  const [recentDocuments, setRecentDocuments] = React.useState<DocumentWithType[]>([]);
  const [docsExpanded, setDocsExpanded] = React.useState(false);
  const [remindersExpanded, setRemindersExpanded] = React.useState(false);

  const { fetchRecentDocs, userReminders, agencyReminders } = useDocumentContext();
  const { hasPermission } = useEnhancedAuth();
  const isManagerOrAdmin = hasPermission('user_manage_agency');

  React.useEffect(() => {
    if (userDetails) {
      fetchRecentDocs(userDetails.id)
        .then(docs => {
          setRecentDocuments(docs);
        })
        .catch(err => {
          error('Error fetching recent documents', err, {
            userId: userDetails.id,
            component: 'DocumentsOverview',
          });
        });
    }
  }, [userDetails, fetchRecentDocs]);

  const remindersData = isManagerOrAdmin
    ? (agencyReminders ?? { documentReminders: [], fieldReminders: [] })
    : { documentReminders: (userReminders as any)?.documentReminders ?? [], fieldReminders: (userReminders as any)?.fieldReminders ?? [] };
  const reminderTotal =
    (Array.isArray(remindersData.documentReminders) ? remindersData.documentReminders.length : 0) +
    (Array.isArray(remindersData.fieldReminders) ? remindersData.fieldReminders.length : 0);

  return (
    <div>
      <div className={styles.documentManagementHeader}>
        <h1 className={styles.documentManagementTitle}>Documents Overview</h1>
        <p className={styles.documentManagementSubtitle}>Welcome to the Documents Overview section. Here you can see a summary of your documents.</p>
      </div>
      <div className={styles.documentManagementSection}>
        <OverviewHeader/>

        <div className={styles.overviewAccordionStack}>
          {/* Recent Documents accordion */}
          <div className={styles.overviewAccordionPanel}>
            <button
              type="button"
              className={styles.overviewAccordionHeader}
              onClick={() => setDocsExpanded(v => !v)}
              aria-expanded={docsExpanded}
            >
              <span className={styles.overviewAccordionTitle}>
                📄 Recent Documents
                <span className={styles.overviewAccordionBadge}>{recentDocuments.length}</span>
              </span>
              <span className={`${styles['expand-arrow']} ${docsExpanded ? styles['expanded'] : ''}`}>▼</span>
            </button>
            {docsExpanded && (
              <div className={styles.overviewAccordionBody}>
                <RecentDocuments recentDocuments={recentDocuments} />
              </div>
            )}
          </div>

          {/* Reminders accordion */}
          <div className={styles.overviewAccordionPanel}>
            <button
              type="button"
              className={styles.overviewAccordionHeader}
              onClick={() => setRemindersExpanded(v => !v)}
              aria-expanded={remindersExpanded}
            >
              <span className={styles.overviewAccordionTitle}>
                🔔 Reminders
                {reminderTotal > 0 && (
                  <span className={`${styles.overviewAccordionBadge} ${styles.overviewAccordionBadgeAlert}`}>
                    {reminderTotal}
                  </span>
                )}
              </span>
              <span className={`${styles['expand-arrow']} ${remindersExpanded ? styles['expanded'] : ''}`}>▼</span>
            </button>
            {remindersExpanded && (
              <div className={styles.overviewAccordionBody}>
                <UserReminders variant="inline" onReminderClick={onReminderClick} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsOverview;


